import * as React from "react";
import { Helmet } from "react-helmet";
import { StaticQuery, graphql } from "gatsby";
import "../styles/base.css";
import "../styles/fonts.css";
import "../styles/print.css";
import Header from "../components/Header";
import LayoutRoot from "../components/LayoutRoot";
import LayoutMain from "../components/LayoutMain";
import {
  ImageType,
  LocalizedType,
  NavigationEntryType,
  BackgroundColorCustomType,
  BackgroundColorType,
} from "../types/Types";
import { buildLocalBusinessJsonLd, JsonLdObject } from "../utils/jsonLd";
import Footer from "../components/Footer";
import { useEffect, useState } from "react";
import styled from "styled-components";
import NavigationMenu from "../components/NavigationMenu";
import { breakpoints, heights } from "../styles/variables";
import LanguageChooser from "../components/LanguageChooser";
import _ from "lodash";
import LinkAlternateLocalized from "../components/LinkAlternateLocalized";

const LayoutWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  column-gap: 50px;
  position: relative;
`;
const Sidebar = styled.div`
  position: fixed;
  inset: 0 0 auto 0;
  display: none;
  z-index: 9;
  padding: ${heights.header}px 17px 35px 17px;
  background: darkgrey;

  &.is-open {
    display: block;
    background: ;
  }

  @media screen and (min-width: ${breakpoints.md}px) {
    position: static;
    padding: ${heights.header}px 0;
    width: 180px;
    display: block;
    background: transparent;

    &.is-open {
      background: transparent;
    }
  }
`;
const StickyContainer = styled.div`
  @media screen and (min-width: ${breakpoints.md}px) {
    position: sticky;
    top: 35px;
  }
`;
interface StaticQueryProps {
  site: {
    siteMetadata: {
      title: string;
      description: string;
      siteUrl: string;
    };
  };
  cms: {
    navigationEntries: NavigationEntryType[];
    globalSet: any;
    languageChooser: any;
  };
  title: string;
  seoTitle?: string;
  description?: string;
  seoDescription?: string;
  image?: ImageType;
  id: string;
  url: string;
  backgroundColor: BackgroundColorType;
  backgroundColorCustom: BackgroundColorCustomType;
  backgroundImage: ImageType;
  localized: LocalizedType;
}

interface DefaultLayoutProps {
  children?: React.ReactNode;
  siteId: number;
  id: string | number;
  url: string;
  language: string;
  backgroundColor: BackgroundColorType;
  backgroundColorCustom: BackgroundColorCustomType;
  backgroundImage: any;
  title?: string;
  seoTitle?: string;
  description?: string;
  seoDescription?: string;
  image?: ImageType[];
  localized: LocalizedType[];
  typeHandle?: string;
  className?: string;
  jsonLd?: (siteUrl: string) => JsonLdObject | JsonLdObject[];
}

const DefaultLayout: React.FC<DefaultLayoutProps> = ({
  children,
  siteId,
  id,
  url,
  language,
  backgroundColor,
  backgroundColorCustom,
  backgroundImage,
  title,
  seoTitle,
  description,
  seoDescription,
  image,
  localized,
  jsonLd,
}) => {
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  return (
    <StaticQuery
      query={graphql`
        query DefaultLayoutQuery {
          site {
            siteMetadata {
              title
              description
              siteUrl
            }
          }
          cms {
            globalSet {
              ... on CMS_footerMenu_GlobalSet {
                id
                name
                globalNavigationMenu(siteId: [1, 2, 3, 4]) {
                  id
                  uri
                  title
                  language
                  siteId
                }
              }
            }
            navigationEntries: entries(
              section: ["pages"]
              level: 1
              siteId: [1, 2, 3, 4]
            ) {
              siteId
              id
              uri
              title
              language
              ... on CMS_pages_Entry {
                id
                showInNavigation
              }
              items: children {
                title
                uri
                id
                ... on CMS_pages_Entry {
                  id
                  showInNavigation
                }
              }
            }
            languageChooser: entries(
              section: ["pages", "events"]
              siteId: [1, 2, 3, 4]
            ) {
              siteId
              id
              uri
              language
            }
          }
        }
      `}
      render={(data: StaticQueryProps) => {
        const navEntriesPerLanguage = data.cms.navigationEntries.filter(
          (item) => item.siteId === siteId
        );
        const navEntriesPerId = data.cms.languageChooser.filter(
          (item: { id: string }) => item.id === id
        );
        const footerMenu = data.cms.globalSet.globalNavigationMenu.filter(
          (item: { siteId: any }) => item.siteId === siteId
        );

        const titleString = () => {
          return title ? `${title} – Militärkantine St.Gallen` : "";
        };

        const resolvedTitle = seoTitle || titleString();
        const resolvedDescription = seoDescription || description;

        const siteUrl = data.site.siteMetadata.siteUrl;
        const localBusinessJsonLd = buildLocalBusinessJsonLd(siteUrl);
        const extraJsonLd = jsonLd ? jsonLd(siteUrl) : [];
        const jsonLdSchemas = [
          localBusinessJsonLd,
          ...(Array.isArray(extraJsonLd) ? extraJsonLd : [extraJsonLd]),
        ];

        return (
          <LayoutRoot
            backgroundColor={backgroundColor}
            backgroundColorCustom={backgroundColorCustom}
            backgroundImage={backgroundImage}
          >
            <LinkAlternateLocalized
              localized={localized}
              currentUrl={url}
              language={language}
            />

            <Helmet>
              <html lang={language} />

              <title>{resolvedTitle}</title>
              <meta property="og:title" content={resolvedTitle} />
              <meta name="twitter:title" content={resolvedTitle} />
              {resolvedDescription && (
                <meta name="description" content={resolvedDescription} />
              )}
              {resolvedDescription && (
                <meta property="og:description" content={resolvedDescription} />
              )}
              {resolvedDescription && (
                <meta
                  property="twitter:description"
                  content={resolvedDescription}
                />
              )}
              {image && image.length > 0 && (
                <meta property="og:image" content={image[0].url} />
              )}
              {image && image.length > 0 && (
                <meta property="twitter:image" content={image[0].url} />
              )}

              <link
                rel="apple-touch-icon"
                sizes="180x180"
                href="/assets/favicon/apple-touch-icon.png"
              />
              <link
                rel="icon"
                type="image/png"
                sizes="32x32"
                href="/assets/favicon/favicon-32x32.png"
              />
              <link
                rel="icon"
                type="image/png"
                sizes="16x16"
                href="/assets/favicon/favicon-16x16.png"
              />
              <link rel="manifest" href="/assets/favicon/site.webmanifest" />
              <link
                rel="mask-icon"
                href="/assets/favicon/safari-pinned-tab.svg"
                color="#5bbad5"
              />
              <link rel="shortcut icon" href="/assets/favicon/favicon.ico" />
              <meta name="msapplication-TileColor" content="#f5e4ef" />
              <meta
                name="msapplication-config"
                content="/assets/favicon/browserconfig.xml"
              />
              <meta name="theme-color" content="#f5e4ef"></meta>
              {jsonLdSchemas.map((schema, index) => {
                const scriptProps: any = {
                  type: "application/ld+json",
                  innerHTML: JSON.stringify(schema, null, 2),
                };
                return <script key={index} {...scriptProps} />;
              })}
              <script
                async
                defer
                src="https://static.foratable.com/iframeContainers.js"
              ></script>
            </Helmet>
            <Header
              isMobileNavOpen={isMobileNavOpen}
              setIsMobileNavOpen={setIsMobileNavOpen}
              language={language ? language : "en"}
            />
            <LayoutWrapper className="layout-wrapper">
              <Sidebar className={isMobileNavOpen ? "is-open" : ""}>
                <StickyContainer>
                  <NavigationMenu
                    items={navEntriesPerLanguage}
                    isMobileNavOpen={isMobileNavOpen}
                    navEntriesPerId={navEntriesPerId}
                  />
                  <LanguageChooser navEntriesPerId={navEntriesPerId} />
                </StickyContainer>
              </Sidebar>
              <LayoutMain>{children}</LayoutMain>
            </LayoutWrapper>
            <Footer
              navigationMenu={footerMenu}
              isShareOpen={isShareOpen}
              setIsShareOpen={setIsShareOpen}
            />
          </LayoutRoot>
        );
      }}
    />
  );
};

export default DefaultLayout;
